import { create } from 'zustand'
import { evaluate, describeError, CELL, boardLetters, type Puzzle, type Tile, type Evaluation } from '@/lib/scoring'
import { saveDay, type DayRecord } from '@/lib/history'

export interface PlacedTile {
  row: number
  col: number
  rackIndex: number
}

export type Direction = 'across' | 'down'

/** Where typed letters go (keyboard play). */
export interface Cursor {
  row: number
  col: number
  dir: Direction
}

interface GameState {
  puzzle: Puzzle | null
  board: string[][]
  record: DayRecord | null
  /** Display order of rack tiles, as indices into puzzle.player_letters. */
  rackOrder: number[]
  placed: PlacedTile[]
  selected: number | null
  cursor: Cursor | null
  message: string | null
  isWord: ((w: string) => boolean) | null
  /** The placement the player has been asked to confirm (see placementKey). */
  pending: string | null
  /** Which play the board shows once the day is finished. */
  view: 'mine' | 'top'
  /** Whether progress is saved to the browser (off for the tutorial). */
  persist: boolean
  /** The tutorial's pulsing highlight: a rack tile or a square ("row,col"). */
  coach: { rackIndex?: number; cell?: string } | null
  setCoach: (coach: GameState['coach']) => void

  start: (puzzle: Puzzle, record: DayRecord, options?: { persist?: boolean }) => void
  setDictionary: (words: Set<string>) => void
  select: (rackIndex: number | null) => void
  place: (row: number, col: number) => void
  recallAt: (row: number, col: number) => void
  recallAll: () => void
  shuffle: () => void
  /** Moves a rack letter to another position in the rack (an index into rackOrder). */
  moveInRack: (rackIndex: number, position: number) => void
  /** There is one play per puzzle: the first call asks for confirmation, the second plays. */
  submit: () => void
  setView: (view: 'mine' | 'top') => void
  /**
   * Drag and drop: puts a rack tile or a placed tile on a square, or back on the rack (to = null).
   * Dropped on one of the player's placed tiles, a placed tile swaps squares with it and a rack
   * tile replaces it, sending it back to the rack.
   */
  dropTile: (from: { rackIndex: number } | { row: number; col: number }, to: { row: number; col: number } | null) => void
  markSubmitted: () => void
  markStartedSent: () => void
  /** Puts the best placement found so far back on the board. */
  restoreBest: () => void
  setCursor: (row: number, col: number) => void
  moveCursor: (dr: number, dc: number) => void
  toggleDirection: () => void
  typeLetter: (letter: string) => void
  backspace: () => void
}

export function tilesOf(state: Pick<GameState, 'puzzle' | 'placed'>): Tile[] {
  const letters = state.puzzle?.player_letters ?? ''
  return state.placed.map((p) => ({ row: p.row, col: p.col, letter: letters[p.rackIndex] }))
}

const step = (dir: Direction) => (dir === 'across' ? [0, 1] : [1, 0])

/**
 * The first square at or after (row, col) along dir that can take a tile,
 * skipping letters already on the board. Null if a blocked square or the edge
 * comes first.
 */
export function nextOpen(
  state: Pick<GameState, 'puzzle' | 'board' | 'placed'>,
  row: number,
  col: number,
  dir: Direction,
): { row: number; col: number } | null {
  const { puzzle, board, placed } = state
  if (!puzzle) return null
  const [dr, dc] = step(dir)
  for (let r = row, c = col; r < puzzle.board_height && c < puzzle.board_width; r += dr, c += dc) {
    if (puzzle.board_layout[r][c] === CELL.blocked) return null
    if (!board[r][c] && !placed.some((p) => p.row === r && p.col === c)) return { row: r, col: c }
  }
  return null
}

/**
 * Remembers `placed` as the day's best-found placement if it is valid and
 * beats the previous best. Returns the state update to apply.
 */
function rememberIfBest(state: GameState, placed: PlacedTile[]): Partial<GameState> {
  const { puzzle, record, isWord } = state
  if (!puzzle || !record || !isWord || placed.length === 0) return {}
  const result = evaluate(puzzle, tilesOf({ puzzle, placed }), isWord)
  if (!result.ok || result.play.score <= (record.bestFound?.score ?? 0)) return {}
  const next = { ...record, bestFound: { tiles: result.play.tiles, words: result.play.words, score: result.play.score } }
  if (state.persist) saveDay(next)
  return { record: next }
}

/**
 * The tiles of the day's play, for showing it on the board. Days played before
 * tiles were saved fall back to the best placement found, if that was the play.
 */
export function playedTiles(record: DayRecord | null): Tile[] {
  const play = record?.attempts.at(-1)
  if (!play) return []
  if (play.tiles) return play.tiles
  const best = record?.bestFound
  return best && best.score === play.score && best.words.join() === play.words.join() ? best.tiles : []
}

/** Whether the tiles on the board are exactly the best-found placement. */
export function showingBest(state: Pick<GameState, 'puzzle' | 'placed' | 'record'>): boolean {
  const best = state.record?.bestFound
  if (!best || best.tiles.length !== state.placed.length) return false
  const current = new Set(tilesOf(state).map((t) => `${t.row},${t.col},${t.letter}`))
  return best.tiles.every((t) => current.has(`${t.row},${t.col},${t.letter}`))
}

/** Identifies a placement, so a confirmation lapses once any tile changes. */
export function placementKey(state: Pick<GameState, 'puzzle' | 'placed'>): string {
  return tilesOf(state)
    .map((t) => `${t.row},${t.col},${t.letter}`)
    .sort()
    .join(' ')
}

/** Whether Play has been pressed once for the tiles now on the board. */
export function awaitingConfirm(state: Pick<GameState, 'puzzle' | 'placed' | 'pending'>): boolean {
  return state.pending !== null && state.placed.length > 0 && state.pending === placementKey(state)
}

/** Evaluates the tiles currently on the board, or null while the dictionary loads. */
export function currentEvaluation(state: GameState): Evaluation | null {
  if (!state.puzzle || !state.isWord || state.placed.length === 0) return null
  return evaluate(state.puzzle, tilesOf(state), state.isWord)
}

export const useGameState = create<GameState>((set, get) => ({
  puzzle: null,
  board: [],
  record: null,
  rackOrder: [],
  placed: [],
  selected: null,
  cursor: null,
  message: null,
  isWord: null,
  pending: null,
  view: 'mine',
  persist: true,
  coach: null,

  start: (puzzle, record, options) =>
    set({
      persist: options?.persist ?? true,
      coach: null,
      puzzle,
      board: boardLetters(puzzle),
      record,
      rackOrder: [...puzzle.player_letters].map((_, i) => i),
      placed: [],
      selected: null,
      cursor: null,
      message: null,
      pending: null,
      view: 'mine',
    }),

  setDictionary: (words) => set({ isWord: (w: string) => words.has(w) }),

  select: (rackIndex) => {
    const { placed, record } = get()
    if (record?.finished || (rackIndex !== null && placed.some((p) => p.rackIndex === rackIndex))) return
    set({ selected: rackIndex, cursor: null, message: null })
  },

  place: (row, col) => {
    const { puzzle, board, placed, selected, rackOrder, record } = get()
    if (!puzzle || selected === null || record?.finished) return
    const cell = puzzle.board_layout[row]?.[col]
    if (cell === undefined || cell === CELL.blocked || board[row][col]) return
    if (placed.some((p) => p.row === row && p.col === col)) return

    const nextPlaced = [...placed, { row, col, rackIndex: selected }]
    const nextFree = rackOrder.find((i) => !nextPlaced.some((p) => p.rackIndex === i))
    set({
      placed: nextPlaced,
      selected: nextFree ?? null,
      cursor: null,
      message: null,
      ...rememberIfBest(get(), nextPlaced),
    })
  },

  recallAt: (row, col) => {
    const tile = get().placed.find((p) => p.row === row && p.col === col)
    if (!tile) return
    set((s) => {
      const placed = s.placed.filter((p) => p !== tile)
      return { placed, selected: s.selected ?? tile.rackIndex, message: null, ...rememberIfBest(s, placed) }
    })
  },

  recallAll: () => set({ placed: [], selected: null, message: null }),

  shuffle: () =>
    set((s) => {
      const order = [...s.rackOrder]
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[order[i], order[j]] = [order[j], order[i]]
      }
      return { rackOrder: order }
    }),

  moveInRack: (rackIndex, position) =>
    set((s) => {
      const from = s.rackOrder.indexOf(rackIndex)
      const to = Math.max(0, Math.min(s.rackOrder.length - 1, position))
      if (from < 0 || from === to) return {}
      const order = [...s.rackOrder]
      order.splice(from, 1)
      order.splice(to, 0, rackIndex)
      return { rackOrder: order }
    }),

  submit: () => {
    const state = get()
    const { puzzle, record } = state
    if (!puzzle || !record || record.finished) return
    const result = currentEvaluation(state)
    if (!result) {
      set({ message: state.placed.length === 0 ? 'Place some tiles first.' : 'Loading the word list…' })
      return
    }
    if (!result.ok) {
      set({ message: describeError(result) })
      return
    }
    if (!awaitingConfirm(state)) {
      // LetterRack shows the prompt and turns Play into Confirm.
      set({ pending: placementKey(state), message: null })
      return
    }
    const attempts = [...record.attempts, { words: result.play.words, score: result.play.score, tiles: result.play.tiles }]
    const next = { ...record, attempts, finished: true }
    if (get().persist) saveDay(next)
    set({
      record: next,
      placed: [],
      selected: null,
      cursor: null,
      pending: null,
      message: `${result.play.words.join(', ')} scored ${result.play.score}!`,
    })
  },

  setView: (view) => set({ view }),

  setCoach: (coach) => set({ coach }),

  dropTile: (from, to) => {
    const { puzzle, board, placed, record } = get()
    if (!puzzle || record?.finished) return
    // A tile already placed on the target square: the dropped tile takes its place.
    const there = to && placed.find((p) => p.row === to.row && p.col === to.col)
    if ('rackIndex' in from) {
      if (!to) return
      if (there) {
        // From the rack: the placed tile goes back to the rack.
        const next = placed.map((p) => (p === there ? { ...p, rackIndex: from.rackIndex } : p))
        set({ placed: next, selected: null, cursor: null, message: null, ...rememberIfBest(get(), next) })
        return
      }
      get().select(from.rackIndex)
      get().place(to.row, to.col)
      return
    }
    const tile = placed.find((p) => p.row === from.row && p.col === from.col)
    if (!tile) return
    if (!to) {
      get().recallAt(from.row, from.col)
      return
    }
    if (there) {
      // From the board: the two tiles swap squares.
      if (there === tile) return
      const next = placed.map((p) =>
        p === tile ? { ...p, row: to.row, col: to.col } : p === there ? { ...p, row: from.row, col: from.col } : p,
      )
      set({ placed: next, cursor: null, message: null, ...rememberIfBest(get(), next) })
      return
    }
    const free =
      puzzle.board_layout[to.row]?.[to.col] !== undefined &&
      puzzle.board_layout[to.row][to.col] !== CELL.blocked &&
      !board[to.row][to.col] &&
      !placed.some((p) => p.row === to.row && p.col === to.col)
    if (!free) return
    const next = placed.map((p) => (p === tile ? { ...p, row: to.row, col: to.col } : p))
    set({ placed: next, cursor: null, message: null, ...rememberIfBest(get(), next) })
  },

  markSubmitted: () => {
    const { record } = get()
    if (!record) return
    const next = { ...record, submitted: true }
    if (get().persist) saveDay(next)
    set({ record: next })
  },

  markStartedSent: () => {
    const { record } = get()
    if (!record) return
    const next = { ...record, startedSent: true }
    if (get().persist) saveDay(next)
    set({ record: next })
  },

  setCursor: (row, col) => {
    const { cursor, record, puzzle } = get()
    if (!puzzle || record?.finished || puzzle.board_layout[row][col] === CELL.blocked) return
    if (cursor?.row === row && cursor.col === col) {
      get().toggleDirection()
      return
    }
    set({ cursor: { row, col, dir: cursor?.dir ?? 'across' }, selected: null, message: null })
  },

  moveCursor: (dr, dc) => {
    const { cursor, puzzle, record } = get()
    if (!puzzle || record?.finished) return
    const dir: Direction = dc !== 0 ? 'across' : 'down'
    const from = cursor ?? { row: 0, col: 0 }
    const row = Math.min(puzzle.board_height - 1, Math.max(0, from.row + (cursor ? dr : 0)))
    const col = Math.min(puzzle.board_width - 1, Math.max(0, from.col + (cursor ? dc : 0)))
    set({ cursor: { row, col, dir }, selected: null, message: null })
  },

  toggleDirection: () =>
    set((s) => (s.cursor ? { cursor: { ...s.cursor, dir: s.cursor.dir === 'across' ? 'down' : 'across' } } : {})),

  typeLetter: (letter) => {
    const state = get()
    const { puzzle, cursor, placed, rackOrder, record } = state
    if (!puzzle || record?.finished) return
    if (!cursor) {
      set({ message: 'Click a square to start typing.' })
      return
    }
    const rackIndex = rackOrder.find(
      (i) => puzzle.player_letters[i] === letter && !placed.some((p) => p.rackIndex === i),
    )
    if (rackIndex === undefined) {
      set({ message: `No ${letter} left in your letters.` })
      return
    }
    const target = nextOpen(state, cursor.row, cursor.col, cursor.dir)
    if (!target) {
      set({ message: 'No room that way. Press Space to switch direction.' })
      return
    }
    const nextPlaced = [...placed, { ...target, rackIndex }]
    const [dr, dc] = step(cursor.dir)
    const after = nextOpen({ ...state, placed: nextPlaced }, target.row + dr, target.col + dc, cursor.dir)
    set({
      placed: nextPlaced,
      cursor: { ...(after ?? target), dir: cursor.dir },
      selected: null,
      message: null,
      ...rememberIfBest(state, nextPlaced),
    })
  },

  backspace: () => {
    const { placed, cursor, record } = get()
    const last = placed[placed.length - 1]
    if (!last || record?.finished) return
    const nextPlaced = placed.slice(0, -1)
    set({
      placed: nextPlaced,
      cursor: { row: last.row, col: last.col, dir: cursor?.dir ?? 'across' },
      selected: null,
      message: null,
      ...rememberIfBest(get(), nextPlaced),
    })
  },

  restoreBest: () => {
    const { puzzle, record } = get()
    const best = record?.bestFound
    if (!puzzle || !best || record.finished) return
    const used = new Set<number>()
    const placed: PlacedTile[] = []
    for (const t of best.tiles) {
      const rackIndex = [...puzzle.player_letters].findIndex((l, i) => l === t.letter && !used.has(i))
      if (rackIndex < 0) return
      used.add(rackIndex)
      placed.push({ row: t.row, col: t.col, rackIndex })
    }
    set({ placed, selected: null, cursor: null, message: null })
  },
}))
